import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent } from './shared/RichTextContent';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

interface PageBlurbProps {
  /** Property field name for the blurb (e.g. 'property_floorplans_blurb') */
  blurbField?: string;
  /** Unique prefix so each page's blurb token is independently editable */
  tokenPrefix?: string;
  /** Static fallback text when no property blurb is available */
  defaultText?: string;
}

export function PageBlurb({ blurbField, tokenPrefix, defaultText }: PageBlurbProps) {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const textPath = tokenPrefix ? `${tokenPrefix}.text` : 'pageblurb.text';

  return (
    <section ref={sectionRef} className="reveal-fade-up py-10 md:py-14 bg-[#F5F6F7]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <RichTextContent
          path={textPath}
          defaultField={blurbField}
          defaultValue={defaultText || ''}
          as="p"
          className="font-body text-base md:text-lg leading-relaxed text-[var(--vibe-color-text)] [&_a]:text-primary [&_a]:underline"
        />
      </div>

      {/* Schema-lint anchor for static analysis — hidden at runtime */}
      <RichTextContent
        path="pageblurb.text"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PageBlurb',
  displayName: 'Page Blurb',
  tokens: {
    'pageblurb.text': {
      label: 'Blurb Text',
      type: 'richtext',
      defaultValue: '',
    },
  },
};

(PageBlurb as any).contentSchema = contentSchema;

export default PageBlurb;
